import Link from 'next/link';
import { ArrowRight, Shield, DollarSign, Users, Bus, Smartphone, Receipt, Clock, Bell, FileText, type LucideIcon } from 'lucide-react';

interface ParentsSectionProps {
  guardianLoginUrl?: string;
}

interface RoleItem {
  title: string;
  description: string;
  iconName: string;
}

const iconMap: Record<string, LucideIcon> = {
  Shield,
  DollarSign,
  Users,
  Bus,
  Smartphone,
  Receipt,
  Clock,
  Bell,
  FileText,
};

const parentRoles: RoleItem[] = [
  {
    title: 'Fees and receipts that stay organized',
    description:
      'View current dues and upcoming fee cycles; pay via QR or reference transfer and upload proofs; download receipts and statements anytime.',
    iconName: 'Receipt',
  },
  {
    title: 'Attendance and academics at a glance',
    description:
      'Daily attendance summaries with history, exam schedules and admit cards, plus event and holiday calendars synced with the school.',
    iconName: 'Clock',
  },
  {
    title: 'Conversations that feel natural',
    description:
      'Submit and track leave requests, send feedback to school teams, and receive notices and attachments with read-anywhere access.',
    iconName: 'Bell',
  },
  {
    title: 'WhatsApp + App, together',
    description:
      'Automated nudges for pending fees, share payment proofs via WhatsApp bot that confirm in-app, and multilingual templates for inclusive communication.',
    iconName: 'Smartphone',
  },
  {
    title: 'Adoption tips for schools',
    description:
      'Launch campaign toolkit, step-by-step onboarding checklists for staff, and a parent FAQ handout covering payments and support.',
    iconName: 'FileText',
  },
  {
    title: 'Trust and privacy',
    description:
      'Parents see only authorised wards and documents; data encrypted in transit and clear opt-in settings for notifications and WhatsApp messaging.',
    iconName: 'Shield',
  },
];

export function ParentsSection({ guardianLoginUrl }: ParentsSectionProps) {
  return (
    <div className="px-3 xs:px-4 sm:px-6 md:px-8 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-20 bg-white mx-2 sm:mx-4 lg:mx-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 xs:mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-[#1D2125] mb-4 xs:mb-5 sm:mb-6">
            Parents in the loop, without chasing the school.
          </h2>
          <p className="text-sm xs:text-base sm:text-lg text-[#4D4F52] max-w-xs xs:max-w-sm sm:max-w-2xl md:max-w-3xl mx-auto leading-relaxed px-2 xs:px-4 sm:px-0">
            The Nest Parent App keeps families informed about fees, attendance, events, and notices—backed by WhatsApp workflows for quick updates.
          </p>
        </div>

        <div className="grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xs:gap-7 sm:gap-8 lg:gap-10 mb-8 xs:mb-10">
          {parentRoles.map((role, index) => {
            const IconComponent = iconMap[role.iconName] || Shield;
            return (
              <div key={index} className="text-left">
                <div className="mb-3 xs:mb-4 flex justify-start">
                  <div className="w-12 xs:w-13 sm:w-14 h-12 xs:h-13 sm:h-14 rounded-lg xs:rounded-xl bg-[#E2FDCB] flex items-center justify-center">
                    <IconComponent className="h-6 xs:h-6.5 sm:h-7 w-6 xs:w-6.5 sm:w-7 text-[#0D373D]" strokeWidth={2} />
                  </div>
                </div>

                <h3 className="text-base xs:text-lg sm:text-xl font-bold text-[#1D2125] mb-2 xs:mb-2.5 sm:mb-3">
                  {role.title}
                </h3>

                <p className="text-sm xs:text-sm sm:text-base text-[#4D4F52] leading-relaxed">
                  {role.description}
                </p>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

